/**
 * GTM tracker components
 * React components that render null and fire dataLayer events via useEffect.
 * Ported from Tracker-frontend/app/components/functional/analytics/gtm-tracker.tsx
 *
 * Identity (`userId` / `isCustomerB2b`) is read from AnalyticsIdentityContext and
 * handed to sendEvent as its own argument, never mixed into the params — see those
 * files for why.
 */

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  sendEvent,
  sendFlatEvent,
  cartLinesToItems,
  cartLinesValue,
  confirmationLinesToItems,
} from "./gtm-events";
import { takeRecoAttribution } from "./reco-attribution";
import { useAnalyticsIdentity } from "./AnalyticsIdentityContext";
import type { ProductPageData } from "@/types/product";
import type { ItemCard } from "@/types/content";
import type { Cart, CartLine, DeliveryType } from "@/types/cart";
import type { OrderConfirmationData } from "@/api/checkout";

// ---------------------------------------------------------------------------
// PageViewTracker
// ---------------------------------------------------------------------------

interface PageViewTrackerProps {
  content: { id: string; type: string };
}

/**
 * Fires a `page_view` event whenever the content or pathname changes.
 */
export function PageViewTracker({ content }: PageViewTrackerProps) {
  const { pathname } = useLocation();
  const identity = useAnalyticsIdentity();

  useEffect(() => {
    if (!content?.id) return;
    sendEvent(
      "page_view",
      {
        content_id: content.id,
        content_type: content.type,
        pathname,
      },
      identity
    );
  }, [content.id, content.type, pathname, identity]);

  return null;
}

// ---------------------------------------------------------------------------
// ProductTracker
// ---------------------------------------------------------------------------

interface ProductTrackerProps {
  product: ProductPageData;
}

/**
 * Fires a `view_item` event when a product page is viewed.
 * Uses a 500ms debounce to ensure the page is fully loaded.
 */
export function ProductTracker({ product }: ProductTrackerProps) {
  const identity = useAnalyticsIdentity();

  useEffect(() => {
    if (!product?.id) return;

    const timer = setTimeout(() => {
      const price = product.compositions?.product?.price;
      const isBundle = product.type?.includes("bundle");

      const items = isBundle
        ? (product.includedProducts ?? []).map((item, index) => ({
            index,
            item_id: item.compositions?.product?.sku ?? "",
            item_name: item.name,
            item_category: "",
            item_variant: "",
            item_product_type: item.compositions?.product?.productType ?? "",
            price: item.compositions?.product?.price?.calculatedPrice ?? 0,
          }))
        : [
            {
              index: 0,
              item_id: product.compositions?.product?.sku ?? "",
              item_name: product.name,
              item_category:
                product.compositions?.productContent?.mainCategory?.name ?? "",
              item_variant: "",
              item_product_type:
                product.compositions?.product?.productType ?? "",
              price: price?.calculatedPrice ?? 0,
            },
          ];

      sendEvent(
        "view_item",
        {
          content_id: product.id,
          content_type: product.type,
          currency: price?.currency ?? "",
          items,
          screen_name: product.name,
          value: price?.calculatedPrice ?? 0,
        },
        identity
      );
    }, 500);

    return () => clearTimeout(timer);
  }, [
    product?.id,
    product?.type,
    product?.name,
    product?.compositions?.product?.price,
    product?.compositions?.product?.sku,
    product?.compositions?.product?.productType,
    product?.compositions?.productContent?.mainCategory?.name,
    product?.includedProducts,
    identity,
  ]);

  return null;
}

// ---------------------------------------------------------------------------
// ProductListingTracker
// ---------------------------------------------------------------------------

interface ProductListingTrackerProps {
  products: ItemCard[];
}

/**
 * Fires a `view_item_list` event when a product listing is viewed.
 */
export function ProductListingTracker({
  products,
}: ProductListingTrackerProps) {
  const identity = useAnalyticsIdentity();

  useEffect(() => {
    if (!products?.length) return;
    sendEvent(
      "view_item_list",
      {
        items: products.map((product, index) => ({
          currency: product.price?.currency ?? "",
          index,
          item_category: product.productCategory ?? "",
          item_id: product.sku ?? "",
          item_name: product.header,
          price: product.price?.calculatedPrice ?? 0,
        })),
      },
      identity
    );
  }, [products, identity]);

  return null;
}

// ---------------------------------------------------------------------------
// CheckoutTracker
// ---------------------------------------------------------------------------

interface CheckoutTrackerProps {
  cart: Cart;
}

/**
 * Fires a `begin_checkout` event when the checkout page loads.
 */
export function CheckoutTracker({ cart }: CheckoutTrackerProps) {
  const identity = useAnalyticsIdentity();

  useEffect(() => {
    if (!cart?.lines?.length) return;
    sendEvent(
      "begin_checkout",
      {
        coupon: cart.discountCodes?.join(" - ") ?? "",
        currency: cart.currency ?? "",
        delivery_type: cart.deliveryType ?? undefined,
        items: cartLinesToItems(cart.lines),
        value: cartLinesValue(cart.lines),
      },
      identity
    );
  }, [cart, identity]);

  return null;
}

// ---------------------------------------------------------------------------
// PurchaseTracker
// ---------------------------------------------------------------------------

/**
 * The cart as it was submitted, captured before the order is placed — the cart is
 * cleared server-side immediately afterwards. Feeds the funnel events that describe
 * the submission (`add_payment_info`, `add_shipping_info`); `purchase` reports the
 * finalised order instead (see PurchaseTracker).
 */
export interface OrderSnapshot {
  lines: CartLine[];
  currency: string;
  discountCodes: string[];
  /** The cart's `deliveryType`; null before anything is in the cart. */
  deliveryType: DeliveryType | null;
}

/** sessionStorage key prefix for the once-per-order guard. */
const PURCHASE_PUSHED_KEY = "gtm_purchase_pushed";

interface PurchaseTrackerProps {
  /** The finalised order, exactly as the confirmation card renders it. */
  order: OrderConfirmationData;
}

/**
 * Fires a `purchase` event for a finalised order. Mounted by every confirmation
 * surface (the shared OrderConfirmationView, which covers the physical-inline,
 * digital and redirect-return flows, plus the B2B invoice confirmation), so any
 * completed order is reported the same way.
 *
 * Everything comes from the order the backend booked, not from the submitted cart:
 * `transaction_id` is the Umbraco order number (with the order GUID alongside as
 * `umbraco_order_id`), and `value`/`tax`/`shipping` are the order's own amounts, so
 * order-level discounts and B2B's zero-VAT net pricing are reported as invoiced.
 */
export function PurchaseTracker({ order }: PurchaseTrackerProps) {
  const identity = useAnalyticsIdentity();

  useEffect(() => {
    // The order number is assigned during finalisation, so until it exists there is
    // no transaction to report. The numeric total gates the event too: a payload
    // without it means the FE is running against an older backend, and a
    // zero-revenue transaction in GA4 can't be taken back.
    if (!order?.orderNumber || typeof order.totalAmount !== "number") return;

    // Once per order rather than once per mount: the confirmation surfaces
    // re-render while polling, and reloading the Stripe return URL mounts them
    // again for an order that was already reported.
    const guardKey = `${PURCHASE_PUSHED_KEY}:${order.orderNumber}`;
    if (sessionStorage.getItem(guardKey)) return;
    sessionStorage.setItem(guardKey, "true");

    // Items added from a My Account recommendation module keep the module as
    // their list, so the purchase is credited to it. Read past the guard only,
    // so a reload of a reported order cannot use up an attribution.
    const lineItems = confirmationLinesToItems(order.lines);
    const listNames = takeRecoAttribution(
      lineItems.map((item) => item.item_id)
    );
    const items = lineItems.map((item) => {
      const listName = listNames[item.item_id];
      return listName
        ? { ...item, item_list_name: listName, item_list_id: listName }
        : item;
    });

    sendEvent(
      "purchase",
      {
        coupon: order.discountCodes?.join(" - ") ?? "",
        currency: order.currency,
        transaction_id: order.orderNumber,
        umbraco_order_id: order.orderId,
        tax: order.taxAmount,
        shipping: order.shippingAmount,
        delivery_type: order.deliveryType ?? undefined,
        items,
        value: order.totalAmount,
      },
      identity
    );
  }, [order, identity]);

  return null;
}

// ---------------------------------------------------------------------------
// LoginTracker
// ---------------------------------------------------------------------------

interface LoginTrackerProps {
  isUserAuthenticated: boolean;
}

/**
 * Fires a `login` event once per session when the user becomes authenticated.
 * Uses sessionStorage to prevent duplicate events.
 */
export function LoginTracker({ isUserAuthenticated }: LoginTrackerProps) {
  const identity = useAnalyticsIdentity();

  useEffect(() => {
    if (!isUserAuthenticated) {
      sessionStorage.removeItem("gtm_login_pushed");
      return;
    }
    if (sessionStorage.getItem("gtm_login_pushed")) return;
    sessionStorage.setItem("gtm_login_pushed", "true");

    sendFlatEvent("login", {}, identity);
  }, [isUserAuthenticated, identity]);

  return null;
}
